import React, { useState } from 'react';
import useWorkflowAiEditor from '../../hooks/useWorkflowAiEditor';

const SIDEBAR_WIDTH = 392;

export default function WorkflowAiPanel({ workflowId, workflowSteps, onStepsApplied }) {
  const [chatText, setChatText] = useState('');
  const editor = useWorkflowAiEditor({
    workflowId,
    workflowSteps,
    onStepsApplied,
  });

  const submitMessage = () => {
    const value = chatText.trim();
    if (!value || editor.loading) return;
    setChatText('');
    editor.sendMessage(value);
  };

  return (
    <div
      className="bg-white border-start d-flex flex-column"
      style={{ width: SIDEBAR_WIDTH, minHeight: 0, overflow: 'hidden', height: '100%' }}
    >
      <div className="px-3 pt-3 pb-2 flex-shrink-0 border-bottom bg-white">
        <h5 className="fw-semibold mb-1">AI Editor</h5>
        <p className="text-muted small mb-0">
          Describe the workflow you want. Changes apply to the canvas; use Save in the toolbar to persist.
        </p>
      </div>

      <div className="flex-grow-1 overflow-auto px-3 py-3" style={{ minHeight: 0 }}>
        {editor.error && (
          <div className="alert alert-danger mb-3" role="alert">
            {editor.error}
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center mb-2">
          <label className="form-label form-label-caps mb-0">Conversation</label>
          {editor.lastMeta?.model && <small className="text-muted">{editor.lastMeta.model}</small>}
        </div>

        <div className="overflow-auto border rounded p-2 bg-light mb-3" style={{ minHeight: 220 }}>
          {editor.messages.length === 0 ? (
            <p className="text-muted small mb-0">
              Ask the AI to build steps, add branches, include form capture, or refine instructions.
            </p>
          ) : (
            editor.messages.map((m, idx) => (
              <div
                key={`${m.role}-${idx}`}
                className={`mb-2 ${m.role === 'user' ? 'text-end' : ''}`}
              >
                <span
                  className={`d-inline-block px-2 py-1 rounded small ${
                    m.role === 'user' ? 'bg-primary text-white' : 'bg-white border'
                  }`}
                >
                  {m.content}
                </span>
              </div>
            ))
          )}
        </div>

        {editor.validationErrors?.length > 0 && (
          <div className="alert alert-warning small py-2" role="alert">
            {editor.validationErrors.join(' | ')}
          </div>
        )}

        <div className="mb-2">
          <textarea
            className="form-control mb-2"
            rows={4}
            placeholder="Ask AI to modify the workflow... (Ctrl+Enter to send)"
            value={chatText}
            onChange={(e) => setChatText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && !editor.loading) {
                e.preventDefault();
                submitMessage();
              }
            }}
            style={{ resize: 'vertical', minHeight: '5rem' }}
          />
          <div className="d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-outline-primary"
              disabled={editor.loading || !chatText.trim() || !workflowId}
              onClick={submitMessage}
            >
              {editor.loading ? '...' : 'Send'}
            </button>
          </div>
        </div>
      </div>

      <div className="flex-shrink-0 bg-light border-top p-3">
        <p className="text-muted small mb-0">
          Select a node on the canvas to edit step properties manually.
        </p>
      </div>
    </div>
  );
}
